<template>
  <ul role="list" class="-mx-2 space-y-1">
    <template v-for="item in navigation">
      <li v-if='item.visible' :key="item.name">
        <a :href='item.href' target='_blank' rel='noopener noreferer' v-if='item.external' class='text-jananavy-200 hover:bg-janaemer-400 hover:text-white group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold'>
          <component :is="item.icon" :class="[isMenuItemActive(item) ? 'text-white' : 'text-jananavy-200 group-hover:text-white', 'h-6 w-6 shrink-0']" aria-hidden="true" />
          {{ item.name }}
        </a>
        <template v-else>
          <ContextualRouterLink v-if="!item.children" :to="item.href" :class="[isMenuItemActive(item) ? 'bg-janaemer-400 text-white' : 'text-jananavy-200 hover:bg-janaemer-400 hover:text-white', 'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold']">
            <component :is="item.icon" :class="[isMenuItemActive(item) ? 'text-white' : 'text-jananavy-200 group-hover:text-white', 'h-6 w-6 shrink-0']" aria-hidden="true" />
            {{ item.name }}
          </ContextualRouterLink>
          <Disclosure as="div" v-else v-slot="{ open }" :defaultOpen='hasActiveSubMenuItem(item)'>
            <DisclosureButton :class="[isMenuItemActive(item) ? 'bg-janaemer-400 text-white' : 'text-jananavy-200 hover:bg-janaemer-400 hover:text-white', 'group flex w-full items-center gap-x-3 rounded-md p-2 text-left text-sm/6 font-semibold']">
              <component :is="item.icon" :class="[isMenuItemActive(item) ? 'text-white' : 'text-jananavy-200 group-hover:text-white', 'h-6 w-6 shrink-0']" aria-hidden="true" />
              {{ item.name }}
              <ChevronRightIcon :class="[open ? 'rotate-90 text-white-500' : 'text-white-400', 'ml-auto h-5 w-5 shrink-0']" aria-hidden="true" />
            </DisclosureButton>
            <DisclosurePanel as="ul" class="mt-1 px-2 space-y-1">
              <template v-for="subItem in item.children">
                <li v-if='subItem.visible' :key="subItem.name">
                  <!-- 44px -->
                  <ContextualRouterLink :to="subItem.href" :class="[isMenuItemActive(subItem) ? 'bg-janaemer-400 text-white' : 'text-jananavy-200 hover:bg-janaemer-400 hover:text-white', 'block rounded-md py-2 pl-9 pr-2 text-sm/6 text-gray-700']">{{ subItem.name }}</ContextualRouterLink>
                </li>
              </template>
            </DisclosurePanel>
          </Disclosure>
        </template>
      </li>
    </template>
  </ul>
</template>

<script setup>

import { computed } from 'vue';

import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
import { ChevronRightIcon } from '@heroicons/vue/20/solid';

import ContextualRouterLink from '@/components/links/contextual-router-link.vue';

import {
  ChartPieIcon,
  HomeIcon,
  UsersIcon,
  ClipboardDocumentCheckIcon,
  Square3Stack3DIcon,
  CircleStackIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  BookOpenIcon,
  NewspaperIcon,
  CloudArrowUpIcon,
  BuildingLibraryIcon,
  QuestionMarkCircleIcon
} from '@heroicons/vue/24/outline';

import { config } from '@/kernel/config';

import { shouldShowSuperAdmin, shouldShowOrganisation } from '@/kernel/session';

const props = defineProps({
  activeMenuItem: String,
  activeSubMenuItem: String
});

const navigation = computed(() => ([
  { name: 'Home', href: '/', icon: HomeIcon, activeKey: 'home', visible: true },

  {
    name: 'Reports', href: '/report', icon: ChartPieIcon, activeKey: 'report', visible: true, children: [
      { name: 'Portfolio – Historical', href: '/report/historical-portfolio', activeKey: 'report-historical-portfolio', visible: true },
      { name: 'Portfolio – Annual', href: '/report/annual-portfolio', activeKey: 'report-annual-portfolio', visible: true },
      { name: 'Asset – Historical', href: '/report/historical-asset', activeKey: 'report-historical-asset', visible: true },
      { name: 'Asset – Annual', href: '/report/annual-asset', activeKey: 'report-annual-asset', visible: true },
      { name: 'Asset Manager – Historical', href: '/report/historical-asset-manager', activeKey: 'report-historical-asset-manager', visible: true },
      { name: 'Asset Manager – Annual', href: '/report/annual-asset-manager', activeKey: 'report-annual-asset-manager', visible: true },
      { name: 'Platform Growth', href: '/report/platform-growth', activeKey: 'report-platform-growth', visible: shouldShowSuperAdmin() },
      { name: 'Organisations & Users', href: '/report/organisations-users', activeKey: 'report-organisations-users', visible: shouldShowSuperAdmin() }
    ]
  },

  { name: 'Organisations', href: '/organisation', icon: BriefcaseIcon, activeKey: 'organisation', visible: shouldShowSuperAdmin() },

  { name: 'Themes', href: '/theme', icon: BookOpenIcon, activeKey: 'theme', visible: shouldShowSuperAdmin() },
  { name: 'Objectives', href: '/objective', icon: CheckCircleIcon, activeKey: 'objective', visible: shouldShowSuperAdmin() },
  { name: 'Sources', href: '/source', icon: BuildingLibraryIcon, activeKey: 'source', visible: shouldShowSuperAdmin() },

  { name: 'Platform Assets', href: '/platform-asset', icon: CircleStackIcon, activeKey: 'platform-asset', visible: shouldShowSuperAdmin() },
  { name: 'Platform Asset Managers', href: '/platform-asset-manager', icon: Square3Stack3DIcon, activeKey: 'platform-asset-manager', visible: shouldShowSuperAdmin() },

  { name: 'Announcements', href: '/announcement', icon: NewspaperIcon, activeKey: 'announcement', visible: shouldShowSuperAdmin() },

  { name: 'Users', href: '/user', icon: UsersIcon, activeKey: 'user', visible: shouldShowSuperAdmin() },

  { name: 'Assets', href: '/organisation-asset', icon: CircleStackIcon, activeKey: 'organisation-asset', visible: shouldShowOrganisation() },
  { name: 'Asset Managers', href: '/organisation-asset-manager', icon: Square3Stack3DIcon, activeKey: 'organisation-asset-manager', visible: shouldShowOrganisation() },

  { name: 'Assessment Templates', href: '/assessment-template', icon: ClipboardDocumentIcon, activeKey: 'assessment-template', visible: shouldShowOrganisation() },
  { name: 'Assessments', href: '/assessment', icon: ClipboardDocumentCheckIcon, activeKey: 'assessment', visible: shouldShowOrganisation() },

  { name: 'Content Library', href: '/content-library', icon: BookOpenIcon, activeKey: 'content-library', visible: shouldShowOrganisation() },

  // { name: 'Import', href: '/import', icon: CloudArrowUpIcon, activeKey: 'import', visible: shouldShowSuperAdmin() },

  { name: 'Support', external: true, href: `mailto:${config.supportEmailAddress}`, icon: QuestionMarkCircleIcon, activeKey: 'support', visible: true }
]));

const isMenuItemActive = (item) => {
  return item.activeKey === props.activeMenuItem;
}

const hasActiveSubMenuItem = (item) => {
  if (!item.children) {
    return false;
  }

  for(const subitem of item.children) {
    if (isMenuItemActive(subitem)) {
      return true;
    }
  }

  return false;
}

</script>
